import { ActionMenu } from '@monorepo/shared/componentsV2/menus/ActionMenu';
import { MenuOption } from '@monorepo/shared/componentsV2/menus/Menu';
import { useConfirmDocumentDelete } from '@monorepo/shared/hooks/documents/useConfirmDocumentDelete';
import { useDocumentDownloadActions } from '@monorepo/shared/hooks/documents/useDocumentDownloadActions';
import { useOpenClose } from '@monorepo/shared/hooks/useOpenClose';
import { DocumentFileResponse } from 'mapistry-shared';
import React, { useMemo } from 'react';

interface DocumentFileTableActionsProps {
  document: DocumentFileResponse;
  showEditActions: boolean;
  onDelete: () => Promise<unknown>;
  renderRenameModal: (onClose: () => void) => React.ReactNode;
  renderMoveModal?: (onClose: () => void) => React.ReactNode;
  canMove?: boolean;
}

export function DocumentFileTableActions({
  document,
  showEditActions,
  onDelete,
  renderRenameModal,
  renderMoveModal,
  canMove = true,
}: DocumentFileTableActionsProps) {
  const [isRenameOpen, openRenameModal, closeRenameModal] = useOpenClose();
  const [isMoveOpen, openMoveModal, closeMoveModal] = useOpenClose();

  const { handleDownload, handleCopyLink } = useDocumentDownloadActions({
    document,
  });

  const { handleDelete } = useConfirmDocumentDelete({
    documentName: document.name,
    onDelete,
  });

  const options = useMemo(() => {
    const items: MenuOption[] = [
      {
        displayName: 'Download',
        onClick: handleDownload,
      },
      {
        displayName: 'Copy link',
        onClick: handleCopyLink,
      },
    ];
    if (showEditActions) {
      items.push({
        displayName: 'Rename',
        onClick: openRenameModal,
      });
      if (renderMoveModal) {
        items.push({
          displayName: 'Move',
          onClick: openMoveModal,
          disabled: !canMove,
          tooltip: canMove ? undefined : 'No destination available',
        });
      }
      items.push({ displayName: 'Delete', onClick: handleDelete });
    }
    return items;
  }, [
    canMove,
    handleCopyLink,
    handleDelete,
    handleDownload,
    openMoveModal,
    openRenameModal,
    renderMoveModal,
    showEditActions,
  ]);

  return (
    <>
      <ActionMenu
        altText={`Action menu for ${document.name}`}
        options={options}
      />
      {isRenameOpen && renderRenameModal(closeRenameModal)}
      {isMoveOpen && renderMoveModal?.(closeMoveModal)}
    </>
  );
}
